/**
 * User utility functions for retrieving user-related data from localStorage
 */

import { CURRENT_USER, post } from '@services';
import { BrokerMembershipType, LenderType, useAuthStore } from '@stores';
import { EmailStatusEnum, LenderStatusEnum, RoleEnum } from '@types';

const FAILED_INVITATION_EMAIL_STATUSES = new Set<EmailStatusEnum>([
  EmailStatusEnum.Bounced,
  EmailStatusEnum.Dropped,
  EmailStatusEnum.Failed
]);

export const isBouncedInvitation = (
  status: LenderStatusEnum,
  emailStatus?: EmailStatusEnum | null
): boolean =>
  [LenderStatusEnum.Pending, LenderStatusEnum.InvitationExpired].includes(
    status
  ) &&
  emailStatus !== null &&
  emailStatus !== undefined &&
  FAILED_INVITATION_EMAIL_STATUSES.has(emailStatus);

/**
 * Gets the current user's ID from localStorage
 * @returns The current user's ID, or null if not found
 */
const getCurrentUserId = (): string | null => {
  return localStorage.getItem('userId');
};

export type UserAuth = {
  user: {
    email: string;
    firstName: string;
    lastName: string;
    userId: string;
    lastLogin: string;
    mobile: string;
  };
  selectLender: {
    organisationId: string;
    organisationName: string;
    hasInspections?: boolean;
  };
  lenderBrokers: {
    lenderOrganisationId: string;
    organisationName: string;
    status?: LenderStatusEnum | string;
  }[];
  brokers?: BrokerMembershipType[];
  lenders: {
    organisationId: string;
    organisationName: string;
    roles?: string[];
  }[];
};

type LenderRecord = UserAuth['lenders'][number] & {
  hasInspections?: boolean;
  hasAssetVerifications?: boolean;
  hasBuyback?: boolean;
};

export const getUserAuth = (): UserAuth | null => {
  return JSON.parse(localStorage.getItem('auth') || '{}')?.state;
};

let currentUserPromise: Promise<UserAuth | null> | null = null;

export const getCurrentUser = async (): Promise<UserAuth | null> => {
  if (currentUserPromise) {
    return currentUserPromise;
  }

  currentUserPromise = fetchCurrentUser();

  return currentUserPromise.finally(() => {
    currentUserPromise = null;
  });
};

const fetchCurrentUser = async (): Promise<UserAuth | null> => {
  try {
    const resp = await post(CURRENT_USER, {});
    if (resp?.lenderBrokers) {
      localStorage.setItem(`lenderBrokers_${resp?.user?.userId}`, JSON.stringify(resp.lenderBrokers));
      useAuthStore.getState().setLenderBrokers(resp.lenderBrokers);
    }
    if (resp) {
      const brokers = resp.brokers ?? [];
      if (resp.user?.userId) {
        localStorage.setItem(
          `brokers_${resp.user.userId}`,
          JSON.stringify(brokers)
        );
      }
      useAuthStore.getState().setBrokers(brokers);
    }
    if (resp?.lenders) {
      localStorage.setItem(`lenders_${resp.user.userId}`, JSON.stringify(resp.lenders));
      useAuthStore.getState().setLenders(resp.lenders);
    }
    return resp || null;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
};

/**
 * Gets the user roles from localStorage
 * @returns Array of user roles, or empty array if not found or on error
 */
const getUserRoles = (): string[] => {
  try {
    let roles: string[] = [];
    const userId = getCurrentUserId();
    if (!userId) {
      return roles;
    }

    const manageRoles = localStorage.getItem(`lenders_${userId}`);
    if (!manageRoles) {
      return roles;
    }

    const parsedRoles = JSON.parse(manageRoles);
    if (
      parsedRoles &&
      Array.isArray(parsedRoles) &&
      parsedRoles.length > 0 &&
      parsedRoles[0].roles &&
      Array.isArray(parsedRoles[0].roles)
    ) {
      roles = [...roles, ...parsedRoles[0].roles];
    }

    const lenderBrokersData = localStorage.getItem(`lenderBrokers_${userId}`);
    if (lenderBrokersData) {
      const parsedLenderBrokers = JSON.parse(lenderBrokersData);
      if (parsedLenderBrokers && Array.isArray(parsedLenderBrokers) && parsedLenderBrokers.length > 0) {
        if (!roles.includes(RoleEnum.LENDER_BROKER)) {
          roles.push(RoleEnum.LENDER_BROKER);
        }
      }
    }

    return roles;
  } catch (error) {
    console.error('Failed to get user roles from localStorage:', error);
    return [];
  }
};

/**
 * Gets the current user's role from localStorage
 * @returns The first role from the user's lender data, or null if not found
 */
const getCurrentUserRole = (): string | null => {
  const roles = getUserRoles();
  return roles?.length > 0 ? roles[0] : null;
};

/**
 * Checks if the user has the given role
 * @param role - The role to check for
 * @returns True if the user has the role, false otherwise
 */
const isUserRoleHas = (role: RoleEnum): boolean => {
  const roles = getUserRoles();
  return roles?.length > 0 && roles?.includes(role);
};

/**
 * Checks if the user has no roles
 * @returns True if the user has no roles, false otherwise
 */
const isUserRolesEmpty = (): boolean => {
  const roles = getUserRoles();
  return roles.length === 0;
};

/**
 * Checks if the current user has the admin role
 * @returns True if the user is an admin, false otherwise
 */
const isAdmin = (): boolean => {
  return isUserRoleHas(RoleEnum.ADMIN);
};

/**
 * Checks if the user is a broker user
 * @returns True if the user is a broker initial setup, false otherwise
 */
const isBrokerInitialSetup = (): boolean => {
  const roles = getUserRoles();
  return roles.length === 0;
};


/**
 * Gets the lenders data from localStorage
 * @returns Lender data array, or empty array if not found or on error
 */
const getLenders = (): LenderRecord[] => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      return [];
    }

    const lendersData = localStorage.getItem(`lenders_${userId}`);
    if (!lendersData) {
      return [];
    }

    const parsed = JSON.parse(lendersData);
    if (parsed && Array.isArray(parsed)) {
      return parsed;
    }

    return [];
  } catch (error) {
    console.error('Failed to get lenders data from localStorage:', error);
    return [];
  }
};

const getLenderForOrganisation = (organisationId?: string | null): LenderRecord | null => {
  if (!organisationId) {
    return null;
  }

  const lenders = getLenders();
  return lenders.find((lender) => lender.organisationId === organisationId) || null;
};

const getSelectedLenderAccess = (
  organisationId = getSelectedLender()?.organisationId
): { isLender: boolean; roles: string[] } => {
  const lender = getLenderForOrganisation(organisationId);

  if (!lender) {
    return { isLender: false, roles: [] };
  }

  return {
    isLender: true,
    roles: Array.isArray(lender.roles) ? lender.roles : []
  };
};

/**
 * Gets the lender brokers data from localStorage
 * @returns Lender brokers data array, or empty array if not found or on error
 */
const getLenderBrokers = (): any[] => {
  try {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      return [];
    }

    const lenderBrokersData = localStorage.getItem(`lenderBrokers_${userId}`);
    if (!lenderBrokersData) {
      return [];
    }
  } catch (error) {
    console.error('Failed to get lender brokers data from localStorage:', error);
    return [];
  }
};

/**
 * Checks if the user has active lender brokers
 * @returns True if the user has active lender brokers, false otherwise
 */
const hasActiveLenderBrokers = async (): Promise<boolean> => {
  const userData = await getCurrentUser();
  if (!userData) {
    return false;
  }
  const { lenderBrokers } = userData;
  const hasActiveLenderBrokers = lenderBrokers?.length > 0 && lenderBrokers?.some(lenderBroker => lenderBroker.status === LenderStatusEnum.Active);
  return hasActiveLenderBrokers;
};

const hasActiveLenders = async (): Promise<boolean> => {
  const userData = await getCurrentUser();
  if (!userData) {
    return false;
  }
  const { lenders } = userData;
  return lenders?.length > 0;
};

const enableInspections = (): boolean => {
  return useAuthStore.getState().organisationConfig.hasInspections && useAuthStore.getState().selectedOrganisationId !== '';
};

/**
 * Gets the currently selected organisation ID from the auth store.
 * @returns {string} The selected organisation ID, or an empty string if not set.
 */
export const getSelectedOrganisationId = (): string => {
  const { selectedOrganisationId } = useAuthStore.getState();
  return selectedOrganisationId || '';
};


const getSelectedLender = (): {organisationId: string, organisationName: string, hasInspections: boolean} | null => {
  const { selectedOrganisationId, organisationConfig, lenders, lenderBrokers } = useAuthStore.getState();
  if (!selectedOrganisationId) {
    return null;
  }

  let selectedLender = null;

  selectedLender = lenders.find((lender) => lender.organisationId === selectedOrganisationId) || null;

  if (!selectedLender) {
    selectedLender = lenderBrokers.find((lenderBroker) => lenderBroker.lenderOrganisationId === selectedOrganisationId) || null;
    if (selectedLender) {
      selectedLender = {
        ...selectedLender,
        organisationId: selectedLender.lenderOrganisationId,
        organisationName: selectedLender.lenderOrganisationName,
      };
    }
  }

  if (!selectedLender) {
    return null;
  }

  return {
    ...organisationConfig,
    ...selectedLender,
  };
};

/**
 * Gets sorted lenders and lender brokers for the lender select page
 * @returns Sorted lenders and lender brokers
 */
export const getSortedLenders = (): LenderType[] => {
  const { lenders, lenderBrokers } = useAuthStore.getState();

  // Map over lenderBrokers to normalize key to organisationName
  const normalizedLenderBrokers = lenderBrokers.filter((broker: any) => broker.status === LenderStatusEnum.Active).map((broker: any) => ({
    ...broker,
    organisationName: broker.lenderOrganisationName || broker.organisationName,
    organisationId: broker.lenderOrganisationId || broker.organisationId,
  }));

  const sortedLenders = [...normalizedLenderBrokers, ...lenders].sort((a: any, b: any) => {
    if (a.organisationName && b.organisationName) {
      return a.organisationName.localeCompare(b.organisationName);
    }
    return 0;
  });
  return sortedLenders as LenderType[];
};

/**
 * A broker needs lender onboarding when they have no lender-org membership
 * and have not requested/registered any lender relationships yet.
 */
export const needsLenderOnboarding = (
  lenders?: { organisationId?: string }[] | null,
  lenderBrokers?: { lenderOrganisationId?: string }[] | null
): boolean => {
  return (lenders?.length ?? 0) === 0 && (lenderBrokers?.length ?? 0) === 0;
};

export {
  getCurrentUserId,
  getCurrentUserRole,
  getUserRoles,
  isAdmin,
  isUserRoleHas,
  getLenderBrokers,
  isBrokerInitialSetup,
  hasActiveLenderBrokers,
  hasActiveLenders,
  enableInspections,
  getSelectedLender,
  getSelectedLenderAccess
};
